import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import {
  effectiveDvrSettings,
  type DeviceHealth,
  type DvrMedium,
  type DvrSettings,
  ACK_NOTE_MAX,
  DB_ERRORS,
  type Alarm,
  type AlarmNotification,
  type SiteAlertSettings,
  type SiteNightContact,
  DEVICE_ID_PATTERN,
  VEHICLE_TYPES,
  parseDbError,
  validateGeofence,
  type Geofence,
  type CurrentUser,
  type Device,
  type Invitation,
  type InvitationPreview,
  type CardStatus,
  type LiveSnapshot,
  type Operator,
  type OperatorCard,
  type OperatorDocument,
  type OperatorPermission,
  type Site,
  type Tenant,
  type TenantRole,
  type Vehicle,
  CHECKLIST_LIMITS,
  CHECKLIST_TOKEN,
  normalizeRecipientEmail,
  validShift,
  validateChecklistTemplate,
  type ChecklistAssignment,
  type ChecklistCategory,
  type ChecklistIssue,
  type ChecklistRecipient,
  type ChecklistTemplate,
  type SiteShift,
  ALERT_TIME_ZONE,
  localDayRange,
  localParts,
  normalizeModules,
  type BatteryType,
  type ChecklistComplianceRow,
  type EventRow,
  type ExportFormat,
  type HeatmapResult,
  type SpaghettiResult,
  type DrivingResult,
  type KpiPanel,
  type KpiPeriod,
  type ReportResult,
  type RoutePoint,
  type UsageRow,
  validPushSubscription,
  type PushSubscriptionInput,
  validKysFormRef,
  validMaintenancePlan,
  validMaintenanceRecord,
  type KysApproverRole,
  type KysFormRef,
  type KysFr11Row,
  type KysFr12Form,
  type MaintenanceRecord,
} from "@fts/core";
import { SwitchableAuthStorage } from "./auth-storage";
import { PartialRegistrationError } from "./partial-registration";
import type { DrivingQuery, EventReportQuery, HeatmapQuery, KysFr11Query, KysFr12Query, ReportQuery, RouteQuery, SpaghettiQuery } from "./reports";
import * as reports from "./supabase-reports";
import * as telematics from "./supabase-telematics";
import type { BatteryQuery, TelematicsQuery } from "./reports";
import type { AnalogCalibrationRecord, DeviceImmobilizer, TeltonikaSyncStatus } from "./provider";
import type { BatteryResult, EcoResult, LiftResult, VehicleAnalogCalibration } from "@fts/core";
import * as kys from "./supabase-kys";
import { configDelivery, lockedSettings, toChargeEvent, toLiftRecord, type ConfigRow, type ModuleSettingsInput, type VehicleModuleData } from "./modules";
import {
  API_ERROR_CODES,
  DataError,
  type AlarmQuery,
  type AlertPerson,
  type AlertSettingsInput,
  type ContactCandidate,
  type EffectiveAlertSettings,
  type EffectiveNightContacts,
  type AlertRecipientsStatus,
  type PersonName,
  type UiLocale,
  type PushConfig,
  type PushDevice,
  type PushTestResult,
  type GeofenceInput,
  type AuthlistStatus,
  type CardInput,
  type DocumentInput,
  type LiveOperator,
  type LiveWatch,
  type OperatorData,
  type OperatorInput,
  type PermissionInput,
  type DataErrorCode,
  type DataProvider,
  type DeviceRegistration,
  type InvitationInput,
  type LiveFeedStatus,
  type Member,
  type RegistrationResult,
  type RevocationResult,
  type RotationResult,
  type SiteInput,
  type Unsubscribe,
  type VehicleInput,
  ChecklistInvalidError,
  type ChecklistForm,
  type ChecklistFault,
  type ChecklistRecipientInput,
  type ChecklistSubmission,
  type ChecklistSubmissionDetail,
  type ChecklistSubmitInput,
  type ChecklistSubmitResult,
  type ChecklistTemplateInput,
  type ChecklistToken,
  type EffectiveChecklistRecipients,
  type FaultQuery,
  type ShiftInput,
  type SubmissionQuery,
  type SignInOptions,
  type MaintenancePlanInput,
  type MaintenanceRecordInput,
} from "./provider";
import {
  GEOFENCE_COLUMNS,
  geofenceToRow,
  toGeofence,
  type GeofenceRow,
  ALARM_COLUMNS,
  ALERT_SETTINGS_COLUMNS,
  NIGHT_CONTACT_COLUMNS,
  NOTIFICATION_COLUMNS,
  alertSettingsToRow,
  toContactCandidate,
  toEffectiveSettings,
  toEffectiveNightContacts,
  toAlertRecipientsStatus,
  type AlertRecipientsStatusRow,
  toPersonName,
  type EffectiveNightContactRow,
  type PersonNameRow,
  type ContactCandidateRow,
  type EffectiveSettingsRow,
  toAlarm,
  toAlertPeople,
  toAlertSettings,
  toNightContact,
  toNotification,
  type AlarmRow,
  type AlertSettingsRow,
  type NightContactRow,
  type NotificationRow,
  AUTHLIST_COLUMNS,
  CARD_COLUMNS,
  DOCUMENT_COLUMNS,
  OPERATOR_COLUMNS,
  PERMISSION_COLUMNS,
  operatorToRow,
  toAuthlistStatus,
  toCard,
  toDocument,
  toOperator,
  toPermission,
  type AuthlistRow,
  type CardRow,
  type DocumentRow,
  type OperatorRow,
  type PermissionRow,
  DEVICE_COLUMNS,
  INVITATION_COLUMNS,
  LIVE_COLUMNS,
  siteToRow,
  toDevice,
  toInvitation,
  toInvitationPreview,
  toLive,
  toMembers,
  toMemberships,
  toSite,
  toTenant,
  toVehicle,
  vehicleToRow,
  type DeviceRow,
  type InvitationPreviewRow,
  type InvitationRow,
  type LiveRow,
  type MembershipRow,
  type MembershipSiteRow,
  type ProfileRow,
  type SiteRow,
  type TenantRow,
  type VehicleRow,
} from "./supabase-mappers";
import {
  ANSWER_COLUMNS,
  ASSIGNMENT_COLUMNS,
  FAULT_COLUMNS,
  ITEM_COLUMNS,
  PHOTO_COLUMNS,
  RECIPIENT_COLUMNS,
  SHIFT_COLUMNS,
  SUBMISSION_COLUMNS,
  TEMPLATE_COLUMNS,
  shiftToRow,
  toAnswer,
  toAssignment,
  toEffectiveRecipients,
  toFault,
  toPhoto,
  toRecipient,
  toShift,
  toSubmission,
  toTemplate,
  toToken,
  type AnswerRow,
  type AssignmentRow,
  type EffectiveRecipientRow,
  type FaultRow,
  type ItemRow,
  type PhotoRow,
  type RecipientRow,
  type ShiftRow,
  type SubmissionRow,
  type TemplateRow,
  type TokenRow,
} from "./checklist-mappers";

type PgError = { message: string; code?: string };
type Res<T> = { data: T | null; error: PgError | null };

type PushDeviceRow = {
  id: string;
  endpoint: string;
  label: string | null;
  created_at: string;
  last_success_at: string | null;
  last_failure_at: string | null;
  failure_count: number | null;
  last_error: string | null;
};

const SITE_COLUMNS = "id, tenant_id, parent_id, name, kind, address, lat, lon";
const VEHICLE_COLUMNS = "id, tenant_id, site_id, fleet_no, type, power, make, model, serial_no, battery_voltage, active, unauthorized_start_alarm, devices(device_id)";

/**
 * Error mapping: `fts:<token>` raised by the database functions first, then permission denied.
 * "No rows" after a filtered update/delete means RLS hid the row, which is also a permission error.
 */
export function mapPgError(error: PgError): DataErrorCode {
  const token = parseDbError(error.message);
  if (token) return token;
  if (error.code === "42501" || error.code === "PGRST116") return "forbidden";
  // A malformed or impossible date (e.g. 2026-02-30 typed in the address bar) reaching a report function.
  if (error.code === "22007" || error.code === "22008") return "invalid_report_range";
  // check_scope_role trigger (tenancy migration) has no token yet.
  if (/remove the site scope before granting admin|admins cannot have a site scope/.test(error.message)) return "invalid_scope";
  return "unknown";
}

/**
 * Supabase Auth sign-in error -> DataErrorCode (ADR 0020). Recognises the CAPTCHA, rate-limit, wrong/expired
 * code and network cases; anything else becomes `fallback`.
 */
export function mapSignInError(error: AuthErrorLike, fallback: DataErrorCode): DataErrorCode {
  const msg = error.message ?? "";
  if (error.code === "captcha_failed" || /captcha/i.test(msg)) return "captcha_failed";
  if (error.status === 429 || /rate_limit/.test(error.code ?? "") || /rate limit|only request this after/i.test(msg)) return "rate_limited";
  if (error.code === "otp_expired" || /token has expired or is invalid|otp.*(expired|invalid)/i.test(msg)) return "invalid_code";
  if (error.name === "AuthRetryableFetchError" || /failed to fetch|network/i.test(msg)) return "network";
  if (/invalid/i.test(msg) && /email/i.test(msg) && !/credentials/i.test(msg)) return "invalid_email";
  return fallback;
}

/** shouldCreateUser: false + unknown address. Treated as success so the form does not reveal accounts. */
function isUnknownUserOtpError(error: AuthErrorLike): boolean {
  return error.code === "otp_disabled" || error.code === "user_not_found" || /signups? not allowed/i.test(error.message ?? "");
}

const API_CODES: ReadonlySet<string> = new Set<string>([...DB_ERRORS, ...API_ERROR_CODES]);

/** Worker API error body `{error: <code>}` -> DataErrorCode; anything unexpected is "unknown". */
export function mapApiError(body: unknown): DataErrorCode {
  const code = body && typeof body === "object" ? (body as { error?: unknown }).error : undefined;
  return typeof code === "string" && API_CODES.has(code) ? (code as DataErrorCode) : "unknown";
}

/** The parts of a Supabase AuthError used for mapping (`code` is set by current GoTrue versions). */
export type AuthErrorLike = { name?: string; message?: string; status?: number; code?: string };

const REAUTH_CODES: ReadonlySet<string> = new Set(["reauthentication_needed", "reauthentication_not_valid", "session_expired", "session_not_found"]);

/** Supabase Auth error of auth.updateUser({ password }) -> DataErrorCode. */
export function mapAuthError(error: AuthErrorLike): DataErrorCode {
  const code = error.code ?? "";
  const message = error.message ?? "";
  if (code === "weak_password" || /password should (be at least|contain)/i.test(message)) return "weak_password";
  if (code === "same_password" || /should be different from the old password/i.test(message)) return "same_password";
  if (REAUTH_CODES.has(code) || error.name === "AuthSessionMissingError" || error.status === 401) return "reauthentication_needed";
  if (code === "over_request_rate_limit" || error.status === 429) return "rate_limited";
  if (error.name === "AuthRetryableFetchError" || error.status === 0) return "network";
  return "unknown";
}

export type SupabaseProviderOptions = {
  /** Worker API base without trailing slash. Default: VITE_API_BASE or "/api". */
  apiBase?: string;
  /** For tests; defaults to the global fetch. */
  fetch?: typeof fetch;
  /** "Remember me" storage; default: browser local/session storage (ignored for the client it is not given to). */
  authStorage?: SwitchableAuthStorage;
};

/**
 * Supabase-backed provider. Uses only the public anon key and the signed-in user's JWT;
 * every query is filtered by Row Level Security (ADR 0005). Never put a service-role key here.
 */
export class SupabaseProvider implements DataProvider {
  readonly mode = "supabase" as const;
  private readonly db: SupabaseClient | null;
  /** "Remember me" storage switch; null with an injected client (tests) (ADR 0020). */
  private readonly authStorage: SwitchableAuthStorage | null;
  private readonly apiBase: string;
  private readonly fetchImpl: typeof fetch | undefined;

  constructor(client?: SupabaseClient, options: SupabaseProviderOptions = {}) {
    this.apiBase = (options.apiBase ?? (import.meta.env.VITE_API_BASE || "/api")).replace(/\/+$/, "");
    this.fetchImpl = options.fetch;
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    this.authStorage = options.authStorage ?? (client ? null : SwitchableAuthStorage.fromWindow());
    // PKCE: e-mail links come back as ?code=… and leave the hash route (#/invite/…) intact.
    this.db =
      client ??
      (url && key
        ? createClient(url, key, {
            auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: "pkce", storage: this.authStorage ?? undefined },
          })
        : null);
  }

  /** Where the next session is stored: localStorage (remember) or sessionStorage. Default: remember. */
  private applyRemember(remember: boolean | undefined): void {
    this.authStorage?.setRemember(remember !== false);
  }

  /** Return target for the sign-in link inside the code e-mail: this console, without a hash route. */
  private static consoleUrl(): string | undefined {
    return typeof window !== "undefined" ? window.location.origin + window.location.pathname : undefined;
  }

  private client(): SupabaseClient {
    if (!this.db) throw new DataError("not_configured", "VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not set");
    return this.db;
  }

  private static check<T>(res: Res<T>): T {
    if (res.error) throw new DataError(mapPgError(res.error), res.error.message);
    return (res.data ?? ([] as unknown)) as T;
  }

  /** For update/delete with `.select()`: zero affected rows means RLS refused the write. */
  private static checkAffected(res: Res<unknown[]>): void {
    const rows = SupabaseProvider.check(res);
    if (!Array.isArray(rows) || rows.length === 0) throw new DataError("forbidden", "no row affected");
  }

  /**
   * POST to the Worker API as the signed-in user (session access token). Returns status and
   * JSON body for 2xx; throws DataError with the API's code otherwise, "unknown" on network errors.
   */
  private async api<T>(path: string, body: unknown): Promise<{ status: number; data: T }> {
    const { data } = await this.client().auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new DataError("unauthorized", "Not signed in");
    const doFetch = this.fetchImpl ?? globalThis.fetch.bind(globalThis);
    let res: Response;
    try {
      res = await doFetch(`${this.apiBase}${path}`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch (e) {
      throw new DataError("unknown", e instanceof Error ? e.message : "network error");
    }
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }
    if (!res.ok) throw new DataError(mapApiError(json), `${path}: HTTP ${res.status}`);
    return { status: res.status, data: json as T };
  }

  private static devicePath(deviceId: string, action: string): string {
    return `/devices/${encodeURIComponent(deviceId)}/${action}`;
  }

  private async uid(): Promise<string> {
    const { data } = await this.client().auth.getUser();
    if (!data.user) throw new DataError("forbidden", "Not signed in");
    return data.user.id;
  }

  async getCurrentUser(): Promise<CurrentUser | null> {
    if (!this.db) return null;
    const { data } = await this.db.auth.getUser();
    const user = data.user;
    if (!user) return null;
    const db = this.db;
    const [profile, admin, memberships, scopes] = await Promise.all([
      db.from("profiles").select("user_id, email, display_name").eq("user_id", user.id).maybeSingle(),
      db.from("platform_admins").select("user_id").eq("user_id", user.id).maybeSingle(),
      db.from("memberships").select("tenant_id, user_id, role").eq("user_id", user.id),
      db.from("membership_sites").select("tenant_id, user_id, site_id").eq("user_id", user.id),
    ]);
    const p = profile.data as ProfileRow | null;
    return {
      id: user.id,
      email: user.email ?? p?.email ?? "",
      displayName: p?.display_name || user.email || "",
      platformAdmin: admin.data !== null,
      memberships: toMemberships(SupabaseProvider.check<MembershipRow[]>(memberships), SupabaseProvider.check<MembershipSiteRow[]>(scopes)),
    };
  }

  async signIn(email: string, password: string, options: SignInOptions = {}): Promise<CurrentUser> {
    const client = this.client();
    this.applyRemember(options.remember);
    const { error } = await client.auth.signInWithPassword({
      email: email.trim(),
      password,
      ...(options.captchaToken ? { options: { captchaToken: options.captchaToken } } : {}),
    });
    if (error) throw new DataError(mapSignInError(error, "invalid_credentials"), error.message);
    const u = await this.getCurrentUser();
    if (!u) throw new DataError("unknown", "signed in but no user");
    return u;
  }

  async sendSignInCode(email: string, options: SignInOptions = {}): Promise<void> {
    const redirectTo = SupabaseProvider.consoleUrl();
    // Stored with the PKCE verifier, so the e-mailed link honours "Remember me" in a new tab too.
    this.applyRemember(options.remember);
    const { error } = await this.client().auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: false,
        ...(redirectTo ? { emailRedirectTo: redirectTo } : {}),
        ...(options.captchaToken ? { captchaToken: options.captchaToken } : {}),
      },
    });
    if (error && !isUnknownUserOtpError(error)) throw new DataError(mapSignInError(error, "unknown"), error.message);
  }

  sessionRemembered(): boolean {
    return (this.authStorage?.persistence ?? "local") === "local";
  }

  async verifySignInCode(email: string, code: string, options: Pick<SignInOptions, "remember"> = {}): Promise<CurrentUser> {
    const client = this.client();
    this.applyRemember(options.remember);
    const { error } = await client.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: "email" });
    if (error) throw new DataError(mapSignInError(error, "invalid_code"), error.message);
    const u = await this.getCurrentUser();
    if (!u) throw new DataError("unknown", "signed in but no user");
    return u;
  }

  async signInDemo(): Promise<CurrentUser> {
    throw new DataError("not_configured", "demo sign-in is only available in demo mode");
  }

  async signOut(): Promise<void> {
    if (this.db) await this.db.auth.signOut();
  }

  async listTenants(): Promise<Tenant[]> {
    const rows = SupabaseProvider.check<TenantRow[]>(await this.client().from("tenants").select("id, name, slug, created_at").order("name"));
    return rows.map(toTenant);
  }

  async listSites(tenantId: string): Promise<Site[]> {
    const rows = SupabaseProvider.check<SiteRow[]>(await this.client().from("sites").select(SITE_COLUMNS).eq("tenant_id", tenantId));
    return rows.map(toSite);
  }

  async listVehicles(tenantId: string): Promise<Vehicle[]> {
    const rows = SupabaseProvider.check<VehicleRow[]>(await this.client().from("vehicles").select(VEHICLE_COLUMNS).eq("tenant_id", tenantId));
    return rows.map(toVehicle);
  }

  async listMembers(tenantId: string): Promise<Member[]> {
    const db = this.client();
    const [m, s] = await Promise.all([
      db.from("memberships").select("tenant_id, user_id, role").eq("tenant_id", tenantId),
      db.from("membership_sites").select("tenant_id, user_id, site_id").eq("tenant_id", tenantId),
    ]);
    const memberships = SupabaseProvider.check<MembershipRow[]>(m);
    const ids = memberships.map((r) => r.user_id);
    const profiles = ids.length
      ? SupabaseProvider.check<ProfileRow[]>(await db.from("profiles").select("user_id, email, display_name").in("user_id", ids))
      : [];
    return toMembers(memberships, SupabaseProvider.check<MembershipSiteRow[]>(s), profiles);
  }

  /* ---------------- fleet master data (direct writes, RLS) ---------------- */

  async createTenant(input: { name: string; slug: string }): Promise<Tenant> {
    const row = SupabaseProvider.check<TenantRow>(
      await this.client().from("tenants").insert({ name: input.name.trim(), slug: input.slug }).select("id, name, slug, created_at").single(),
    );
    return toTenant(row);
  }

  async saveSite(input: SiteInput): Promise<Site> {
    const db = this.client();
    const row = siteToRow(input);
    const res = input.id
      ? await db.from("sites").update(row).eq("id", input.id).select(SITE_COLUMNS).single()
      : await db.from("sites").insert({ ...row, tenant_id: input.tenantId }).select(SITE_COLUMNS).single();
    return toSite(SupabaseProvider.check<SiteRow>(res));
  }

  async deleteSite(siteId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("sites").delete().eq("id", siteId).select("id"));
  }

  async saveVehicle(input: VehicleInput): Promise<Vehicle> {
    const db = this.client();
    const row = vehicleToRow(input);
    const res = input.id
      ? await db.from("vehicles").update(row).eq("id", input.id).select(VEHICLE_COLUMNS).single()
      : await db.from("vehicles").insert({ ...row, tenant_id: input.tenantId }).select(VEHICLE_COLUMNS).single();
    return toVehicle(SupabaseProvider.check<VehicleRow>(res));
  }

  /* ---------------- devices ---------------- */

  async listDevices(tenantId: string): Promise<Device[]> {
    const rows = SupabaseProvider.check<DeviceRow[]>(
      await this.client().from("devices").select(DEVICE_COLUMNS).eq("tenant_id", tenantId).order("device_id"),
    );
    return rows.map(toDevice);
  }

  async listInventory(): Promise<Device[]> {
    const rows = SupabaseProvider.check<DeviceRow[]>(await this.client().from("devices").select(DEVICE_COLUMNS).order("device_id"));
    return rows.map(toDevice);
  }

  async registerDevices(input: DeviceRegistration[]): Promise<RegistrationResult[]> {
    for (const r of input) if (!DEVICE_ID_PATTERN.test(r.deviceId)) throw new DataError("invalid_device_id", r.deviceId);
    type Body = { registered?: RegistrationResult[]; error?: { deviceId?: string; code?: string } };
    const devices = input.map((r) => ({ deviceId: r.deviceId, imei: r.imei ?? undefined, iccid: r.iccid ?? undefined, hw: r.hw ?? undefined }));
    const { status, data } = await this.api<Body | null>("/devices/register", { devices });
    const registered: RegistrationResult[] = (data?.registered ?? []).map((r) => ({
      deviceId: r.deviceId,
      secret: r.secret,
      claimCode: r.claimCode,
      brokerSynced: r.brokerSynced,
    }));
    // 207: the API registers one device per database call and stops at the first failure.
    if (status === 207 || data?.error) {
      const failed = data?.error;
      throw new PartialRegistrationError(mapApiError({ error: failed?.code }), registered, failed?.deviceId ?? "", `register: ${failed?.code ?? "partial"}`);
    }
    return registered;
  }

  async rotateDeviceSecret(deviceId: string): Promise<RotationResult> {
    const { data } = await this.api<RotationResult>(SupabaseProvider.devicePath(deviceId, "rotate"), {});
    return { secret: data.secret, brokerSynced: data.brokerSynced };
  }

  async setDeviceRevoked(deviceId: string, revoked: boolean): Promise<RevocationResult> {
    const { data } = await this.api<RevocationResult>(SupabaseProvider.devicePath(deviceId, "revoke"), { revoked });
    return data.secret ? { secret: data.secret, brokerSynced: data.brokerSynced } : { brokerSynced: data.brokerSynced };
  }

  async releaseDevice(deviceId: string): Promise<{ claimCode: string; brokerSynced: boolean }> {
    const { data } = await this.api<{ claimCode: string; brokerSynced?: boolean }>(SupabaseProvider.devicePath(deviceId, "release"), {});
    return { claimCode: data.claimCode, brokerSynced: data.brokerSynced !== false };
  }

  async claimDevice(tenantId: string, deviceId: string, claimCode: string): Promise<void> {
    SupabaseProvider.check(await this.client().rpc("claim_device", { p_tenant: tenantId, p_device_id: deviceId, p_claim_code: claimCode }));
  }

  async pairDevice(deviceId: string, vehicleId: string): Promise<void> {
    SupabaseProvider.check(await this.client().rpc("pair_device", { p_device_id: deviceId, p_vehicle_id: vehicleId }));
  }

  async unpairDevice(deviceId: string): Promise<void> {
    SupabaseProvider.check(await this.client().rpc("unpair_device", { p_device_id: deviceId }));
  }

  /* ---------------- members and invitations ---------------- */

  async updateMemberRole(tenantId: string, userId: string, role: TenantRole): Promise<void> {
    SupabaseProvider.checkAffected(
      await this.client().from("memberships").update({ role }).eq("tenant_id", tenantId).eq("user_id", userId).select("user_id"),
    );
  }

  async removeMember(tenantId: string, userId: string): Promise<void> {
    SupabaseProvider.checkAffected(
      await this.client().from("memberships").delete().eq("tenant_id", tenantId).eq("user_id", userId).select("user_id"),
    );
  }

  async listInvitations(tenantId: string): Promise<Invitation[]> {
    const rows = SupabaseProvider.check<InvitationRow[]>(
      await this.client().from("invitations").select(INVITATION_COLUMNS).eq("tenant_id", tenantId).order("created_at", { ascending: false }),
    );
    const now = Date.now();
    return rows.map((r) => toInvitation(r, now));
  }

  async createInvitation(input: InvitationInput): Promise<{ invitation: Invitation; token: string }> {
    const db = this.client();
    const res = (await db.rpc("create_invitation", {
      p_tenant: input.tenantId,
      p_email: input.email,
      p_role: input.role,
      p_site_ids: input.siteIds,
    })) as Res<Array<{ invitation_id: string; token: string }>>;
    const rows = SupabaseProvider.check(res);
    const created = Array.isArray(rows) ? rows[0] : (rows as { invitation_id: string; token: string } | null);
    if (!created) throw new DataError("unknown", "empty create_invitation result");
    const row = SupabaseProvider.check<InvitationRow>(
      await db.from("invitations").select(INVITATION_COLUMNS).eq("id", created.invitation_id).single(),
    );
    return { invitation: toInvitation(row), token: created.token };
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    SupabaseProvider.check(await this.client().rpc("revoke_invitation", { p_id: invitationId }));
  }

  async sendInvitationEmail(invitationId: string, token: string): Promise<void> {
    await this.api<{ sent: boolean }>("/invitations/send", { invitationId, token });
  }

  async previewInvitation(token: string): Promise<InvitationPreview | null> {
    const rows = SupabaseProvider.check<InvitationPreviewRow[]>(
      (await this.client().rpc("invitation_preview", { p_token: token })) as Res<InvitationPreviewRow[]>,
    );
    const row = Array.isArray(rows) ? rows[0] : null;
    return row ? toInvitationPreview(row) : null;
  }

  async acceptInvitation(token: string): Promise<string> {
    return SupabaseProvider.check<string>((await this.client().rpc("accept_invitation", { p_token: token })) as Res<string>);
  }

  async sendSignInLink(email: string, redirectTo: string, options: Pick<SignInOptions, "captchaToken"> = {}): Promise<void> {
    // Invitation links: the account may not exist yet, so this one creates it (ADR 0006). Always remembered.
    this.applyRemember(true);
    const { error } = await this.client().auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: redirectTo, shouldCreateUser: true, ...(options.captchaToken ? { captchaToken: options.captchaToken } : {}) },
    });
    if (error) throw new DataError(mapSignInError(error, "unknown"), error.message);
  }

  async changePassword(newPassword: string): Promise<void> {
    let error: AuthErrorLike | null;
    try {
      ({ error } = await this.client().auth.updateUser({ password: newPassword }));
    } catch (e) {
      if (e instanceof DataError) throw e;
      throw new DataError("network", e instanceof Error ? e.message : "network error");
    }
    // Only Supabase's own message is kept; it never contains the password.
    if (error) throw new DataError(mapAuthError(error), error.message);
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const uid = await this.uid();
    SupabaseProvider.checkAffected(
      await this.client().from("profiles").update({ display_name: displayName.trim() }).eq("user_id", uid).select("user_id"),
    );
  }

  /* ---- Sprint 4: live map ---- */

  async listLive(tenantId: string): Promise<LiveSnapshot[]> {
    // Inner join on the vehicle limits the rows to the tenant; RLS already hides everything else.
    const rows = SupabaseProvider.check<LiveRow[]>(
      await this.client().from("device_latest").select(`${LIVE_COLUMNS}, vehicles!inner(tenant_id)`).eq("vehicles.tenant_id", tenantId),
    );
    return rows.map(toLive);
  }

  subscribeLive(onChange: (s: LiveSnapshot) => void, onStatus?: (s: LiveFeedStatus) => void): Unsubscribe {
    const db = this.client();
    onStatus?.("connecting");
    const channel = db
      .channel(`fts-live-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "device_latest" }, (payload) => {
        const row = payload.new as Partial<LiveRow> | null;
        // DELETE events carry only the key; rows are never deleted by design (unpair wipes fields instead).
        if (row && typeof row.device_id === "string" && typeof row.ts === "string") onChange(toLive(row as LiveRow));
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") onStatus?.("live");
        else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") onStatus?.("reconnecting");
      });
    return () => {
      void db.removeChannel(channel);
    };
  }

  /* ---- Sprint 7a: battery and lift modules (ICD v0.2 section 9, ADR 0017) ---- */

  async getVehicleModuleData(vehicleId: string): Promise<VehicleModuleData> {
    const db = this.client();
    const now = new Date();
    const today = localParts(now, ALERT_TIME_ZONE).date;
    const { from: dayStart } = localDayRange(today, today);
    const weekAgo = new Date(now.getTime() - 7 * 86_400_000).toISOString();
    const count = async (type: string) => {
      const res = await db.from("device_events").select("id", { count: "exact", head: true }).eq("vehicle_id", vehicleId).eq("type", type).gte("ts", dayStart!);
      if (res.error) throw new DataError(mapPgError(res.error), res.error.message);
      return res.count ?? 0;
    };
    type EventRow = { id: number; ts: string; type: string; module: Record<string, unknown> | null };
    type VehicleSettingsRow = {
      bat_type: BatteryType | null; bat_ah: number | null; lift_kg_per_bar: number | null; lift_bar_threshold: number | null;
      lift_empty_bar: number | null; rated_capacity_kg: number | null;
    };
    const [veh, devs, series, charges, lifts, overloadsToday, liftsToday] = await Promise.all([
      db.from("vehicles").select("bat_type, bat_ah, lift_kg_per_bar, lift_bar_threshold, lift_empty_bar, rated_capacity_kg").eq("id", vehicleId).maybeSingle(),
      db.from("devices").select("device_id, modules").eq("vehicle_id", vehicleId).limit(1),
      db.rpc("vehicle_soc_series", { p_vehicle: vehicleId }),
      db.from("device_events").select("id, ts, type, module").eq("vehicle_id", vehicleId).in("type", ["charge_start", "charge_end"]).gte("ts", weekAgo).order("ts", { ascending: true }).limit(200),
      db.from("device_events").select("id, ts, type, module").eq("vehicle_id", vehicleId).eq("type", "lift").order("ts", { ascending: false }).limit(10),
      count("overload"),
      count("lift"),
    ]);
    const v = SupabaseProvider.check<VehicleSettingsRow | null>(veh as Res<never>);
    const settings = {
      batType: v?.bat_type ?? null, batAh: v?.bat_ah ?? null, liftKgPerBar: v?.lift_kg_per_bar ?? null, liftBarThreshold: v?.lift_bar_threshold ?? null,
      liftEmptyBar: v?.lift_empty_bar ?? null, ratedCapacityKg: v?.rated_capacity_kg ?? null,
    };
    const dev = SupabaseProvider.check<{ device_id: string; modules: unknown }[]>(devs as Res<never>)[0] ?? null;
    const liftRows = SupabaseProvider.check<EventRow[]>(lifts as Res<never>);
    let battery: VehicleModuleData["battery"] = null;
    let overloadTimes: string[] = [];
    let delivery: VehicleModuleData["delivery"] = configDelivery(settings, null);
    let locked: VehicleModuleData["lockedSettings"] = [];
    let lastDeepDischargeAt: string | null = null;
    if (dev) {
      const oldest = liftRows.at(-1)?.ts;
      const [latestRes, ovlRes, cfgRes, deepRes] = await Promise.all([
        db.from("device_latest").select("soc, bat_v, bat_a, bat_t, bat_at").eq("device_id", dev.device_id).eq("vehicle_id", vehicleId).limit(1),
        oldest
          ? db.from("device_events").select("ts").eq("vehicle_id", vehicleId).eq("type", "overload").gte("ts", new Date(Date.parse(oldest) - 600_000).toISOString()).limit(100)
          : Promise.resolve({ data: [], error: null }),
        db.from("device_module_config").select("config, ver, sent_ver, acked_ver, acked_at, sent_at, rejected_ver, error, inherited").eq("device_id", dev.device_id).limit(1),
        db.from("device_events").select("ts").eq("vehicle_id", vehicleId).eq("type", "deep_discharge").order("ts", { ascending: false }).limit(1),
      ]);
      const latest = SupabaseProvider.check<{ soc: number | null; bat_v: number | null; bat_a: number | null; bat_t: number | null; bat_at: string | null }[]>(latestRes as Res<never>)[0];
      if (latest && latest.bat_at) battery = { soc: latest.soc, batV: latest.bat_v, batA: latest.bat_a, batT: latest.bat_t, at: latest.bat_at };
      overloadTimes = SupabaseProvider.check<{ ts: string }[]>(ovlRes as Res<never>).map((r) => r.ts);
      const cfgRow = SupabaseProvider.check<ConfigRow[]>(cfgRes as Res<never>)[0] ?? null;
      delivery = configDelivery(settings, cfgRow);
      locked = lockedSettings(cfgRow);
      lastDeepDischargeAt = SupabaseProvider.check<{ ts: string }[]>(deepRes as Res<never>)[0]?.ts ?? null;
    }
    return {
      deviceId: dev?.device_id ?? null,
      modules: dev && dev.modules != null ? (normalizeModules(dev.modules) ?? null) : null,
      battery,
      socSeries: SupabaseProvider.check<{ bucket_at: string; soc: number }[]>(series as Res<never>).map((r) => ({ at: r.bucket_at, soc: Number(r.soc) })),
      chargeEvents: SupabaseProvider.check<EventRow[]>(charges as Res<never>).map(toChargeEvent),
      lifts: liftRows.map((r) => toLiftRecord(r, overloadTimes)),
      liftsToday,
      overloadsToday,
      settings,
      delivery,
      lockedSettings: locked,
      lastDeepDischargeAt,
    };
  }

  async saveModuleSettings(input: ModuleSettingsInput): Promise<void> {
    SupabaseProvider.checkAffected(
      await this.client()
        .from("vehicles")
        .update({
          bat_type: input.batType, bat_ah: input.batAh, lift_kg_per_bar: input.liftKgPerBar, lift_bar_threshold: input.liftBarThreshold,
          lift_empty_bar: input.liftEmptyBar, rated_capacity_kg: input.ratedCapacityKg,
        })
        .eq("id", input.vehicleId)
        .select("id"),
    );
  }

  /* ---- Sprint 7b: reports and the KPI panel (ADR 0018; SECURITY INVOKER functions, RLS applies) ---- */

  async reportUsage(q: ReportQuery): Promise<ReportResult<UsageRow>> {
    return reports.reportUsage(this.client(), SupabaseProvider.check, q);
  }

  async reportEvents(q: EventReportQuery): Promise<ReportResult<EventRow>> {
    return reports.reportEvents(this.client(), SupabaseProvider.check, q);
  }

  async reportRoute(q: RouteQuery): Promise<ReportResult<RoutePoint>> {
    const db = this.client();
    const v = SupabaseProvider.check<{ site_id: string } | null>(await db.from("vehicles").select("site_id").eq("id", q.vehicleId).maybeSingle());
    if (!v) throw new DataError("forbidden", "vehicle not visible");
    return reports.reportRoute(db, SupabaseProvider.check, q, await reports.vehicleTz(db, v.site_id));
  }

  async reportChecklist(q: ReportQuery): Promise<ReportResult<ChecklistComplianceRow>> {
    return reports.reportChecklist(this.client(), SupabaseProvider.check, q);
  }

  async reportHeatmap(q: HeatmapQuery): Promise<HeatmapResult> {
    return reports.reportHeatmap(this.client(), SupabaseProvider.check, q);
  }

  async exportHeatmap(q: HeatmapQuery, format: ExportFormat): Promise<HeatmapResult> {
    return reports.exportHeatmap(this.client(), SupabaseProvider.check, q, format);
  }

  async reportSpaghetti(q: SpaghettiQuery): Promise<SpaghettiResult> {
    return reports.reportSpaghetti(this.client(), SupabaseProvider.check, q);
  }

  async exportSpaghetti(q: SpaghettiQuery, format: ExportFormat): Promise<SpaghettiResult> {
    return reports.exportSpaghetti(this.client(), SupabaseProvider.check, q, format);
  }

  async reportDriving(q: DrivingQuery): Promise<DrivingResult> {
    return reports.reportDriving(this.client(), SupabaseProvider.check, q);
  }

  async exportDriving(q: DrivingQuery, format: ExportFormat): Promise<DrivingResult> {
    return reports.exportDriving(this.client(), SupabaseProvider.check, q, format);
  }

  /* ---- ADR 0034 ---- */
  async reportBattery(q: BatteryQuery): Promise<BatteryResult> {
    return telematics.reportBattery(this.client(), SupabaseProvider.check, q);
  }
  async exportBattery(q: BatteryQuery, format: ExportFormat): Promise<BatteryResult> {
    return telematics.exportBattery(this.client(), SupabaseProvider.check, q, format);
  }
  async reportLift(q: TelematicsQuery): Promise<LiftResult> {
    return telematics.reportLift(this.client(), SupabaseProvider.check, q);
  }
  async exportLift(q: TelematicsQuery, format: ExportFormat): Promise<LiftResult> {
    return telematics.exportLift(this.client(), SupabaseProvider.check, q, format);
  }
  async reportEco(q: TelematicsQuery): Promise<EcoResult> {
    return telematics.reportEco(this.client(), SupabaseProvider.check, q);
  }
  async exportEco(q: TelematicsQuery, format: ExportFormat): Promise<EcoResult> {
    return telematics.exportEco(this.client(), SupabaseProvider.check, q, format);
  }
  async getTeltonikaStatus(deviceId: string): Promise<TeltonikaSyncStatus> {
    return telematics.teltonikaStatus(this.client(), SupabaseProvider.check, deviceId);
  }
  async getImmobilizer(deviceId: string): Promise<DeviceImmobilizer> {
    return telematics.immobilizer(this.client(), SupabaseProvider.check, deviceId);
  }
  async getAnalogCalibration(vehicleId: string): Promise<AnalogCalibrationRecord> {
    return telematics.analogCalibration(this.client(), SupabaseProvider.check, vehicleId);
  }
  async saveAnalogCalibration(vehicleId: string, cal: VehicleAnalogCalibration): Promise<void> {
    return telematics.saveCalibration(this.client(), SupabaseProvider.check, vehicleId, cal);
  }

  async getKpiPanel(tenantId: string, siteId: string | null, period: KpiPeriod): Promise<KpiPanel> {
    return reports.kpiPanel(this.client(), SupabaseProvider.check, tenantId, siteId, period);
  }

  async exportUsage(q: ReportQuery, format: ExportFormat): Promise<ReportResult<UsageRow>> {
    return reports.exportUsage(this.client(), SupabaseProvider.check, q, format);
  }

  async exportEvents(q: EventReportQuery, format: ExportFormat): Promise<ReportResult<EventRow>> {
    return reports.exportEvents(this.client(), SupabaseProvider.check, q, format);
  }

  async exportRoute(q: RouteQuery, format: ExportFormat): Promise<ReportResult<RoutePoint>> {
    const db = this.client();
    const v = SupabaseProvider.check<{ site_id: string } | null>(await db.from("vehicles").select("site_id").eq("id", q.vehicleId).maybeSingle());
    if (!v) throw new DataError("forbidden", "vehicle not visible");
    return reports.exportRoute(db, SupabaseProvider.check, q, format, await reports.vehicleTz(db, v.site_id));
  }

  async exportChecklist(q: ReportQuery, format: ExportFormat): Promise<ReportResult<ChecklistComplianceRow>> {
    return reports.exportChecklist(this.client(), SupabaseProvider.check, q, format);
  }

  /* ---- ADR 0033: KYS forms ---- */

  async reportKysFr11(q: KysFr11Query): Promise<ReportResult<KysFr11Row>> {
    return kys.reportKysFr11(this.client(), SupabaseProvider.check, q);
  }

  async exportKysFr11(q: KysFr11Query, format: ExportFormat): Promise<ReportResult<KysFr11Row>> {
    return kys.exportKysFr11(this.client(), SupabaseProvider.check, q, format);
  }

  async reportKysFr12(q: KysFr12Query): Promise<ReportResult<KysFr12Form>> {
    return kys.reportKysFr12(this.client(), SupabaseProvider.check, q);
  }

  async exportKysFr12(q: KysFr12Query, format: ExportFormat): Promise<ReportResult<KysFr12Form>> {
    return kys.exportKysFr12(this.client(), SupabaseProvider.check, q, format);
  }

  async approveChecklistSubmission(submissionId: string): Promise<{ approvedAt: string; approvedRole: KysApproverRole }> {
    const db = this.client();
    SupabaseProvider.check((await db.rpc("approve_checklist_submission", { p_submission: submissionId })) as Res<string>);
    const row = SupabaseProvider.check<{ approved_at: string; approved_role: KysApproverRole } | null>(
      (await db.from("checklist_submissions").select("approved_at, approved_role").eq("id", submissionId).maybeSingle()) as Res<{ approved_at: string; approved_role: KysApproverRole }>,
    );
    if (!row) throw new DataError("forbidden", "submission not visible");
    return { approvedAt: new Date(row.approved_at).toISOString(), approvedRole: row.approved_role };
  }

  async setChecklistTemplateForm(templateId: string, form: KysFormRef | null): Promise<void> {
    if (form && !validKysFormRef(form)) throw new DataError("invalid_form", "form reference breaks a rule");
    SupabaseProvider.check(
      await this.client().rpc("set_checklist_template_form", {
        p_template: templateId,
        p_code: form?.code ?? null,
        p_revision: form?.revision ?? null,
        p_revision_date: form?.revisionDate ?? null,
        p_title: form?.titleTr ?? null,
        p_title_en: form?.titleEn ?? "",
      }),
    );
  }

  async listMaintenanceRecords(vehicleId: string): Promise<MaintenanceRecord[]> {
    const rows = SupabaseProvider.check<kys.RecordRow[]>(
      await this.client().from("vehicle_maintenance_records").select(kys.RECORD_COLUMNS).eq("vehicle_id", vehicleId).order("done_on", { ascending: false }).limit(500),
    );
    return kys.toRecords(rows);
  }

  async saveMaintenancePlan(input: MaintenancePlanInput): Promise<void> {
    if (!validMaintenancePlan(input)) throw new DataError("invalid_maintenance", "plan breaks a rule");
    SupabaseProvider.check(
      await this.client()
        .from("vehicle_maintenance_plans")
        .upsert({ vehicle_id: input.vehicleId, interval_months: input.intervalMonths, responsible_role: input.responsibleRole }, { onConflict: "vehicle_id" })
        .select("vehicle_id"),
    );
  }

  async addMaintenanceRecord(input: MaintenanceRecordInput): Promise<MaintenanceRecord> {
    const note = input.note?.trim() || null;
    const voidReason = input.voidReason?.trim() || null;
    // Pre-check with the Istanbul date; the database checks the site's local date.
    if (!validMaintenanceRecord({ ...input, note, voidReason }, localParts(new Date(), ALERT_TIME_ZONE).date)) throw new DataError("invalid_maintenance", "record breaks a rule");
    const rows = SupabaseProvider.check<kys.RecordRow[]>(
      await this.client()
        .from("vehicle_maintenance_records")
        .insert({ vehicle_id: input.vehicleId, kind: input.kind, done_on: input.doneOn, note, voids_record_id: input.voidsRecordId ?? null, void_reason: voidReason })
        .select(kys.RECORD_COLUMNS),
    );
    if (!rows[0]) throw new DataError("forbidden", "record not visible");
    return kys.toRecord(rows[0]);
  }

  async getFormFooter(tenantId: string): Promise<string | null> {
    const row = SupabaseProvider.check<{ form_footer: string | null } | null>(
      (await this.client().from("tenants").select("form_footer").eq("id", tenantId).maybeSingle()) as Res<{ form_footer: string | null }>,
    );
    return row?.form_footer ?? null;
  }

  async setFormFooter(tenantId: string, text: string | null): Promise<void> {
    if ((text?.trim().length ?? 0) > 200) throw new DataError("invalid_form", "footer too long");
    SupabaseProvider.check(await this.client().rpc("set_tenant_form_footer", { p_tenant: tenantId, p_text: text?.trim() || null }));
  }

  /* ---- Sprint 5: operators (direct writes, RLS; triggers enforce the rules) ---- */

  async listOperatorData(tenantId: string): Promise<OperatorData> {
    const db = this.client();
    const [o, c, d, p] = await Promise.all([
      db.from("operators").select(OPERATOR_COLUMNS).eq("tenant_id", tenantId),
      db.from("operator_cards").select(CARD_COLUMNS).eq("tenant_id", tenantId),
      db.from("operator_documents").select(DOCUMENT_COLUMNS).eq("tenant_id", tenantId),
      db.from("operator_permissions").select(PERMISSION_COLUMNS).eq("tenant_id", tenantId),
    ]);
    return {
      operators: SupabaseProvider.check<OperatorRow[]>(o).map(toOperator),
      cards: SupabaseProvider.check<CardRow[]>(c).map(toCard),
      documents: SupabaseProvider.check<DocumentRow[]>(d).map(toDocument),
      permissions: SupabaseProvider.check<PermissionRow[]>(p).map(toPermission),
    };
  }

  async saveOperator(input: OperatorInput): Promise<Operator> {
    const db = this.client();
    const row = operatorToRow(input);
    const res = input.id
      ? await db.from("operators").update(row).eq("id", input.id).select(OPERATOR_COLUMNS).single()
      : await db.from("operators").insert({ ...row, tenant_id: input.tenantId }).select(OPERATOR_COLUMNS).single();
    return toOperator(SupabaseProvider.check<OperatorRow>(res));
  }

  async addCard(input: CardInput): Promise<OperatorCard> {
    const res = await this.client()
      .from("operator_cards")
      .insert({ tenant_id: input.tenantId, operator_id: input.operatorId, uid: input.uid, tech: input.tech })
      .select(CARD_COLUMNS)
      .single();
    return toCard(SupabaseProvider.check<CardRow>(res));
  }

  async setCardStatus(cardId: string, status: Exclude<CardStatus, "active">): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("operator_cards").update({ status }).eq("id", cardId).select("id"));
  }

  async saveDocument(input: DocumentInput): Promise<OperatorDocument> {
    const db = this.client();
    const row = { kind: input.kind, label: input.label?.trim() || null, valid_until: input.validUntil, required: input.required };
    const res = input.id
      ? await db.from("operator_documents").update(row).eq("id", input.id).select(DOCUMENT_COLUMNS).single()
      : await db.from("operator_documents").insert({ ...row, tenant_id: input.tenantId, operator_id: input.operatorId }).select(DOCUMENT_COLUMNS).single();
    return toDocument(SupabaseProvider.check<DocumentRow>(res));
  }

  async deleteDocument(documentId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("operator_documents").delete().eq("id", documentId).select("id"));
  }

  async addPermission(input: PermissionInput): Promise<OperatorPermission> {
    const res = await this.client()
      .from("operator_permissions")
      .insert({
        tenant_id: input.tenantId,
        operator_id: input.operatorId,
        site_id: input.siteId,
        vehicle_type: input.vehicleType,
        vehicle_id: input.vehicleId,
        valid_until: input.validUntil,
      })
      .select(PERMISSION_COLUMNS)
      .single();
    return toPermission(SupabaseProvider.check<PermissionRow>(res));
  }

  async deletePermission(permissionId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("operator_permissions").delete().eq("id", permissionId).select("id"));
  }

  /* ADR 0027: recorder health. RLS: health rows only for devices on vehicles the user can see. */
  async listDeviceHealth(tenantId: string): Promise<DeviceHealth[]> {
    type Dev = { device_id: string; vehicle_id: string | null; has_card_reader: boolean | null; dvr_medium: DvrMedium | null; dvr_cameras: number[] | null };
    type H = { device_id: string; vehicle_id: string | null; reported_at: string; cameras: number[] | null; ipcs: number[] | null; storage: Partial<Record<DvrMedium, number>> | null; video_loss: number | string | null };
    type I = { device_id: string; key: string; state: number; bad_since: string; last_at: string; alarmed_at: string | null };
    const devs = SupabaseProvider.check<Dev[]>(
      await this.client().from("devices").select("device_id, vehicle_id, has_card_reader, dvr_medium, dvr_cameras").eq("tenant_id", tenantId).order("device_id"),
    );
    const ids = devs.map((d) => d.device_id);
    if (ids.length === 0) return [];
    const [hs, is] = await Promise.all([
      this.client().from("device_health").select("device_id, vehicle_id, reported_at, cameras, ipcs, storage, video_loss").in("device_id", ids),
      this.client().from("device_health_issues").select("device_id, key, state, bad_since, last_at, alarmed_at").in("device_id", ids).order("key"),
    ]);
    const health = new Map(SupabaseProvider.check<H[]>(hs).map((h) => [h.device_id, h]));
    const issues = SupabaseProvider.check<I[]>(is);
    return devs.map((d) => {
      const settings: DvrSettings = { medium: d.dvr_medium, cameras: d.dvr_cameras };
      const h = health.get(d.device_id);
      const current = h && h.vehicle_id === d.vehicle_id ? h : undefined;
      return {
        deviceId: d.device_id,
        vehicleId: d.vehicle_id,
        reportedAt: current?.reported_at ?? null,
        cameras: current?.cameras ?? [],
        ipcs: current?.ipcs ?? [],
        storage: current?.storage ?? {},
        videoLoss: current?.video_loss === null || current?.video_loss === undefined ? null : Number(current.video_loss),
        settings,
        effective: effectiveDvrSettings(settings, d.has_card_reader === false),
        issues: current
          ? issues.filter((i) => i.device_id === d.device_id).map((i) => ({ key: i.key, state: i.state, since: i.bad_since, lastAt: i.last_at, alarmed: i.alarmed_at !== null }))
          : [],
      };
    });
  }

  async setDeviceDvrSettings(deviceId: string, settings: DvrSettings): Promise<void> {
    SupabaseProvider.check(await this.client().rpc("set_device_dvr_settings", { p_device: deviceId, p_medium: settings.medium, p_cameras: settings.cameras }));
  }

  async listAuthlistStatus(tenantId: string): Promise<AuthlistStatus[]> {
    const rows = SupabaseProvider.check<AuthlistRow[]>(
      await this.client().from("device_authlist").select(`${AUTHLIST_COLUMNS}, devices!inner(tenant_id)`).eq("devices.tenant_id", tenantId),
    );
    return rows.map(toAuthlistStatus);
  }

  async syncAuthlists(tenantId: string): Promise<{ sent: number; failed: number }> {
    const r = await this.api<{ sent: number; failed: number }>("/authlist/sync", { tenantId });
    return r.data;
  }

  async startLiveWatch(vehicleId: string, durS?: number): Promise<LiveWatch> {
    const r = await this.api<{ vehicleId: string; deviceId: string; until: string }>("/live", durS === undefined ? { vehicleId } : { vehicleId, durS });
    return { vehicleId: r.data.vehicleId, deviceId: r.data.deviceId, until: r.data.until, acked: false };
  }

  async listLiveWatches(tenantId: string): Promise<LiveWatch[]> {
    const rows = SupabaseProvider.check<{ vehicle_id: string; device_id: string; until: string; acked: boolean }[]>(
      await this.client().rpc("active_live_watches", { p_tenant: tenantId }),
    );
    return rows.map((r) => ({ vehicleId: r.vehicle_id, deviceId: r.device_id, until: new Date(r.until).toISOString(), acked: r.acked === true }));
  }

  async listLiveOperators(tenantId: string): Promise<LiveOperator[]> {
    const rows = SupabaseProvider.check<{ device_id: string; operator_id: string; full_name: string }[]>(
      await this.client().rpc("live_operators", { p_tenant: tenantId }),
    );
    return rows.map((r) => ({ deviceId: r.device_id, operatorId: r.operator_id, fullName: r.full_name }));
  }

  /* ---- Sprint 6a: alarms (ADR 0011) ---- */

  async listAlarms(tenantId: string, query: AlarmQuery = {}): Promise<Alarm[]> {
    let q = this.client().from("alarms").select(ALARM_COLUMNS).eq("tenant_id", tenantId);
    if (query.status) q = q.eq("status", query.status);
    if (query.type) q = q.eq("type", query.type);
    if (query.vehicleId) q = q.eq("vehicle_id", query.vehicleId);
    if (query.siteIds) q = q.in("site_id", query.siteIds);
    if (query.from) q = q.gte("occurred_at", query.from);
    if (query.to) q = q.lt("occurred_at", query.to);
    const rows = SupabaseProvider.check<AlarmRow[]>(await q.order("occurred_at", { ascending: false }).order("id", { ascending: false }).limit(query.limit ?? 500));
    return rows.map(toAlarm);
  }

  async getAlarm(tenantId: string, alarmId: string): Promise<Alarm | null> {
    if (!/^\d+$/.test(alarmId)) return null;
    const row = SupabaseProvider.check<AlarmRow | AlarmRow[] | null>(
      (await this.client().from("alarms").select(ALARM_COLUMNS).eq("tenant_id", tenantId).eq("id", alarmId).maybeSingle()) as Res<AlarmRow>,
    );
    // check() turns "no row" (null) into []: an unknown or hidden alarm is null, not an empty alarm.
    return row && !Array.isArray(row) ? toAlarm(row) : null;
  }

  async countOpenAlarms(tenantId: string): Promise<number> {
    const res = await this.client().from("alarms").select("id", { count: "exact", head: true }).eq("tenant_id", tenantId).eq("status", "open");
    if (res.error) throw new DataError(mapPgError(res.error), res.error.message);
    return res.count ?? 0;
  }

  async listAlarmNotifications(alarmId: string): Promise<AlarmNotification[]> {
    const rows = SupabaseProvider.check<NotificationRow[]>(
      await this.client().from("alarm_notifications").select(NOTIFICATION_COLUMNS).eq("alarm_id", alarmId).order("due_at", { ascending: true }),
    );
    return rows.map(toNotification);
  }

  async acknowledgeAlarm(alarmId: string, note: string | null): Promise<void> {
    const trimmed = note?.trim() || null;
    if (trimmed && trimmed.length > ACK_NOTE_MAX) throw new DataError("note_too_long", "note too long");
    if (!/^\d+$/.test(alarmId)) throw new DataError("not_found", "alarm id");
    // bigint parameter: a JSON number is exact up to 2^53, far beyond any alarm id.
    SupabaseProvider.check(await this.client().rpc("acknowledge_alarm", { p_alarm_id: Number(alarmId), p_note: trimmed }));
  }

  async listAlertSettings(tenantId: string): Promise<SiteAlertSettings[]> {
    const rows = SupabaseProvider.check<AlertSettingsRow[]>(
      await this.client().from("site_alert_settings").select(ALERT_SETTINGS_COLUMNS).eq("tenant_id", tenantId),
    );
    return rows.map(toAlertSettings);
  }

  async saveAlertSettings(input: AlertSettingsInput): Promise<SiteAlertSettings> {
    // No upsert: ON CONFLICT DO UPDATE would also set site_id/tenant_id, which are not granted for update.
    const db = this.client();
    const fields = alertSettingsToRow(input);
    const updated = SupabaseProvider.check<AlertSettingsRow[]>(
      await db.from("site_alert_settings").update(fields).eq("site_id", input.siteId).select(ALERT_SETTINGS_COLUMNS),
    );
    if (updated[0]) return toAlertSettings(updated[0]);
    const row = SupabaseProvider.check<AlertSettingsRow>(
      await db
        .from("site_alert_settings")
        .insert({ ...fields, site_id: input.siteId, tenant_id: input.tenantId })
        .select(ALERT_SETTINGS_COLUMNS)
        .single(),
    );
    return toAlertSettings(row);
  }

  async getEffectiveAlertSettings(siteId: string): Promise<EffectiveAlertSettings | null> {
    const rows = SupabaseProvider.check<EffectiveSettingsRow[]>(
      (await this.client().rpc("effective_alert_settings", { p_site: siteId })) as Res<EffectiveSettingsRow[]>,
    );
    const row = Array.isArray(rows) ? rows[0] : null;
    return row ? toEffectiveSettings(row) : null;
  }

  async listContactCandidates(siteId: string): Promise<ContactCandidate[]> {
    const rows = SupabaseProvider.check<ContactCandidateRow[]>(
      (await this.client().rpc("alert_contact_candidates", { p_site: siteId })) as Res<ContactCandidateRow[]>,
    );
    return rows.map(toContactCandidate);
  }

  async resetAlertSettings(siteId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("site_alert_settings").delete().eq("site_id", siteId).select("site_id"));
  }

  async listNightContacts(tenantId: string): Promise<SiteNightContact[]> {
    const rows = SupabaseProvider.check<NightContactRow[]>(
      await this.client().from("site_night_contacts").select(NIGHT_CONTACT_COLUMNS).eq("tenant_id", tenantId),
    );
    return rows.map(toNightContact);
  }

  async setNightContacts(_tenantId: string, siteId: string, userIds: string[]): Promise<void> {
    // One transaction on the server: every user is validated first, then the site's own rows are replaced.
    SupabaseProvider.check(await this.client().rpc("set_night_contacts", { p_site: siteId, p_user_ids: [...new Set(userIds)] }));
  }

  async getEffectiveNightContacts(siteId: string): Promise<EffectiveNightContacts> {
    const rows = SupabaseProvider.check<EffectiveNightContactRow[]>(
      (await this.client().rpc("effective_night_contacts", { p_site: siteId })) as Res<EffectiveNightContactRow[]>,
    );
    return toEffectiveNightContacts(Array.isArray(rows) ? rows : []);
  }

  async getAlertRecipientsStatus(siteId: string): Promise<AlertRecipientsStatus | null> {
    const rows = SupabaseProvider.check<AlertRecipientsStatusRow[]>(
      (await this.client().rpc("site_alert_recipients_status", { p_site: siteId })) as Res<AlertRecipientsStatusRow[]>,
    );
    return toAlertRecipientsStatus(Array.isArray(rows) ? rows : []);
  }

  async getAlarmCopyAdmins(tenantId: string): Promise<boolean> {
    const rows = SupabaseProvider.check<{ alarm_copy_admins: boolean | null }[]>(
      await this.client().from("tenants").select("alarm_copy_admins").eq("id", tenantId),
    );
    return rows[0]?.alarm_copy_admins === true;
  }

  async setAlarmCopyAdmins(tenantId: string, on: boolean): Promise<void> {
    SupabaseProvider.check(await this.client().rpc("set_tenant_alarm_copy_admins", { p_tenant: tenantId, p_on: on }));
  }

  async listAlarmPeople(alarmId: string): Promise<PersonName[]> {
    if (!/^\d+$/.test(alarmId)) return [];
    const rows = SupabaseProvider.check<PersonNameRow[]>(
      (await this.client().rpc("alarm_people", { p_alarm_id: Number(alarmId) })) as Res<PersonNameRow[]>,
    );
    return (Array.isArray(rows) ? rows : []).map(toPersonName);
  }

  async listAlertPeople(tenantId: string): Promise<AlertPerson[]> {
    // RLS: admins read every membership and profile of the tenant; other roles only their own.
    return toAlertPeople(await this.listMembers(tenantId));
  }

  async updateLocale(locale: UiLocale): Promise<void> {
    const uid = await this.uid();
    SupabaseProvider.checkAffected(await this.client().from("profiles").update({ locale }).eq("user_id", uid).select("user_id"));
  }

  /* ---- Sprint 7b: web push (ADR 0019; RLS: own devices only, keys never readable) ---- */

  async getPushConfig(): Promise<PushConfig> {
    const r = await this.publicApi<{ enabled?: unknown; publicKey?: unknown }>("/push/config", { method: "GET" });
    const publicKey = typeof r?.publicKey === "string" && r.publicKey ? r.publicKey : null;
    return { enabled: r?.enabled === true && publicKey !== null, publicKey };
  }

  async listPushDevices(): Promise<PushDevice[]> {
    const uid = await this.uid();
    const rows = SupabaseProvider.check<PushDeviceRow[]>(
      await this.client()
        .from("push_subscriptions")
        .select("id, endpoint, label, created_at, last_success_at, last_failure_at, failure_count, last_error")
        .eq("user_id", uid)
        .order("created_at", { ascending: true }),
    );
    return rows.map((r) => ({
      id: r.id,
      endpoint: r.endpoint,
      label: r.label,
      createdAt: r.created_at,
      lastSuccessAt: r.last_success_at,
      lastFailureAt: r.last_failure_at,
      failureCount: r.failure_count ?? 0,
      lastError: r.last_error,
    }));
  }

  async savePushSubscription(input: PushSubscriptionInput): Promise<string> {
    if (!validPushSubscription(input)) throw new DataError("invalid_push_subscription", "subscription breaks a rule");
    const id = SupabaseProvider.check<string>(
      (await this.client().rpc("push_subscribe", { p_endpoint: input.endpoint, p_p256dh: input.p256dh, p_auth: input.auth, p_label: input.label })) as Res<string>,
    );
    return id;
  }

  async removePushDevice(id: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("push_subscriptions").delete().eq("id", id).select("id"));
  }

  async sendTestPush(): Promise<PushTestResult> {
    const { data } = await this.api<PushTestResult>("/push/test", {});
    return { sent: Number(data?.sent ?? 0), failed: Number(data?.failed ?? 0), removed: Number(data?.removed ?? 0) };
  }

  /* ---- Sprint 6b: geofences (ADR 0015; RLS: read = sees the site, write = safety+ on the site) ---- */

  async listGeofences(tenantId: string, siteId?: string): Promise<Geofence[]> {
    let query = this.client().from("geofences").select(GEOFENCE_COLUMNS).eq("tenant_id", tenantId);
    if (siteId !== undefined) query = query.eq("site_id", siteId);
    const rows = SupabaseProvider.check<GeofenceRow[]>(await query.order("name"));
    return rows.map(toGeofence);
  }


  async getGeofence(geofenceId: string): Promise<Geofence | null> {
    const res = (await this.client().from("geofences").select(GEOFENCE_COLUMNS).eq("id", geofenceId).maybeSingle()) as Res<GeofenceRow>;
    // check() turns "no row" (null) into []; maybeSingle() returns an object or null.
    const row = SupabaseProvider.check<GeofenceRow | GeofenceRow[] | null>(res);
    return row && !Array.isArray(row) ? toGeofence(row) : null;
  }

  async saveGeofence(input: GeofenceInput): Promise<Geofence> {
    if (validateGeofence(input, VEHICLE_TYPES).length > 0) throw new DataError("invalid_geofence", "geofence breaks a rule");
    const db = this.client();
    const row = geofenceToRow(input);
    const res = input.id
      ? await db.from("geofences").update(row).eq("id", input.id).select(GEOFENCE_COLUMNS).single()
      : await db.from("geofences").insert({ ...row, tenant_id: input.tenantId, site_id: input.siteId }).select(GEOFENCE_COLUMNS).single();
    // A table check constraint (23514) that the client-side validation did not catch.
    if (res.error?.code === "23514" && !parseDbError(res.error.message)) throw new DataError("invalid_geofence", res.error.message);
    return toGeofence(SupabaseProvider.check<GeofenceRow>(res as Res<GeofenceRow>));
  }

  async deleteGeofence(geofenceId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("geofences").delete().eq("id", geofenceId).select("id"));
  }

  /* ---- Sprint 7a: pre-shift checklist (ADR 0016) ---- */

  /** Public Worker call of the QR form (no session: the token is the credential). */
  private async publicApi<T>(path: string, init: RequestInit = {}): Promise<T> {
    const doFetch = this.fetchImpl ?? globalThis.fetch.bind(globalThis);
    let res: Response;
    try {
      res = await doFetch(`${this.apiBase}${path}`, init);
    } catch (e) {
      // Offline or unreachable (ADR 0019): the QR form shows "no connection" with a retry button.
      throw new DataError("network", e instanceof Error ? e.message : "network error");
    }
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }
    if (!res.ok) {
      const body = json as { error?: unknown; issues?: unknown } | null;
      if (body?.error === "invalid_checklist") throw new ChecklistInvalidError(Array.isArray(body.issues) ? (body.issues as ChecklistIssue[]) : []);
      throw new DataError(mapApiError(json), `${path}: HTTP ${res.status}`);
    }
    return json as T;
  }

  private static checklistPath(token: string, action = ""): string {
    if (!CHECKLIST_TOKEN.test(token)) throw new DataError("checklist_token_invalid", "malformed token");
    return `/checklist/${token}${action ? `/${action}` : ""}`;
  }

  async getChecklistForm(token: string): Promise<ChecklistForm> {
    return this.publicApi<ChecklistForm>(SupabaseProvider.checklistPath(token), { method: "GET" });
  }

  async uploadChecklistPhoto(token: string, file: Blob): Promise<string> {
    if (file.size > CHECKLIST_LIMITS.photoMaxBytes) throw new DataError("invalid_photo", "photo too large");
    const r = await this.publicApi<{ id: string }>(SupabaseProvider.checklistPath(token, "photos"), {
      method: "POST",
      headers: { "content-type": file.type || "application/octet-stream" },
      body: file,
    });
    return r.id;
  }

  async submitChecklist(token: string, input: ChecklistSubmitInput): Promise<ChecklistSubmitResult> {
    const r = await this.publicApi<{ ok: boolean; submission_id: string }>(SupabaseProvider.checklistPath(token, "submit"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ locale: input.locale, answers: input.answers, clientSubmissionId: input.clientSubmissionId, formVersion: input.formVersion }),
    });
    return { submissionId: r.submission_id };
  }

  async listShifts(tenantId: string): Promise<SiteShift[]> {
    const rows = SupabaseProvider.check<ShiftRow[]>(await this.client().from("site_shifts").select(SHIFT_COLUMNS).eq("tenant_id", tenantId).order("start_time"));
    return rows.map(toShift);
  }

  async saveShift(input: ShiftInput): Promise<SiteShift> {
    if (!validShift(input)) throw new DataError("invalid_shift", "shift breaks a rule");
    const db = this.client();
    const res = input.id
      ? await db.from("site_shifts").update(shiftToRow(input)).eq("id", input.id).select(SHIFT_COLUMNS).single()
      : await db.from("site_shifts").insert({ ...shiftToRow(input), site_id: input.siteId, tenant_id: input.tenantId }).select(SHIFT_COLUMNS).single();
    return toShift(SupabaseProvider.check<ShiftRow>(res as Res<ShiftRow>));
  }

  async deleteShift(shiftId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("site_shifts").delete().eq("id", shiftId).select("id"));
  }

  async listChecklistRecipients(tenantId: string): Promise<ChecklistRecipient[]> {
    const rows = SupabaseProvider.check<RecipientRow[]>(await this.client().from("site_checklist_recipients").select(RECIPIENT_COLUMNS).eq("tenant_id", tenantId));
    return rows.map(toRecipient);
  }

  async addChecklistRecipient(input: ChecklistRecipientInput): Promise<ChecklistRecipient> {
    const email = input.email === null ? null : normalizeRecipientEmail(input.email);
    if (input.email !== null && !email) throw new DataError("invalid_recipient", "invalid e-mail");
    const row = SupabaseProvider.check<RecipientRow>(
      (await this.client()
        .from("site_checklist_recipients")
        .insert({ site_id: input.siteId, tenant_id: input.tenantId, user_id: input.userId, email, label: input.label?.trim() || null })
        .select(RECIPIENT_COLUMNS)
        .single()) as Res<RecipientRow>,
    );
    return toRecipient(row);
  }

  async deleteChecklistRecipient(recipientId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("site_checklist_recipients").delete().eq("id", recipientId).select("id"));
  }

  async getEffectiveChecklistRecipients(siteId: string): Promise<EffectiveChecklistRecipients> {
    const rows = SupabaseProvider.check<EffectiveRecipientRow[]>(
      (await this.client().rpc("effective_checklist_recipients", { p_site: siteId })) as Res<EffectiveRecipientRow[]>,
    );
    return toEffectiveRecipients(Array.isArray(rows) ? rows : []);
  }

  async listChecklistTemplates(tenantId: string): Promise<ChecklistTemplate[]> {
    const db = this.client();
    const templates = SupabaseProvider.check<TemplateRow[]>(
      await db.from("checklist_templates").select(TEMPLATE_COLUMNS).or(`tenant_id.is.null,tenant_id.eq.${tenantId}`).order("name"),
    );
    const ids = templates.map((t) => t.id);
    const items = ids.length ? SupabaseProvider.check<ItemRow[]>(await db.from("checklist_items").select(ITEM_COLUMNS).in("template_id", ids)) : [];
    // System template names are shown translated by category in the console; tenant templates by their own name.
    return templates.map((t) => toTemplate(t, items));
  }

  async copyChecklistTemplate(tenantId: string, templateId: string, name: string | null): Promise<string> {
    return SupabaseProvider.check<string>(
      (await this.client().rpc("copy_checklist_template", { p_template: templateId, p_tenant: tenantId, p_name: name?.trim() || null })) as Res<string>,
    );
  }

  async saveChecklistTemplate(input: ChecklistTemplateInput): Promise<void> {
    if (validateChecklistTemplate(input.name, input.items).length > 0) throw new DataError("invalid_template", "template breaks a rule");
    const items = input.items.map((i) => ({
      ...(i.id ? { id: i.id } : {}),
      code: i.code.trim().toUpperCase(),
      textTr: i.textTr.trim(),
      textEn: i.textEn.trim(),
      critical: i.critical,
      photoRequiredOnFail: i.photoRequiredOnFail,
      powerTypes: i.powerTypes,
      impactFirst: i.impactFirst,
      allowNa: i.allowNa,
    }));
    SupabaseProvider.check(await this.client().rpc("save_checklist_template", { p_template: input.id, p_name: input.name.trim(), p_items: items }));
  }

  async deleteChecklistTemplate(templateId: string): Promise<void> {
    SupabaseProvider.checkAffected(await this.client().from("checklist_templates").delete().eq("id", templateId).select("id"));
  }

  async listChecklistAssignments(tenantId: string): Promise<ChecklistAssignment[]> {
    const rows = SupabaseProvider.check<AssignmentRow[]>(await this.client().from("checklist_assignments").select(ASSIGNMENT_COLUMNS).eq("tenant_id", tenantId));
    return rows.map(toAssignment);
  }

  async setChecklistAssignment(tenantId: string, category: ChecklistCategory, templateId: string | null): Promise<void> {
    const db = this.client();
    if (templateId === null) {
      SupabaseProvider.check(await db.from("checklist_assignments").delete().eq("tenant_id", tenantId).eq("category", category).select("category"));
      return;
    }
    // No upsert: ON CONFLICT DO UPDATE would also write tenant_id/category, which are not granted for update.
    const updated = SupabaseProvider.check<AssignmentRow[]>(
      await db.from("checklist_assignments").update({ template_id: templateId }).eq("tenant_id", tenantId).eq("category", category).select(ASSIGNMENT_COLUMNS),
    );
    if (updated.length) return;
    SupabaseProvider.check(await db.from("checklist_assignments").insert({ tenant_id: tenantId, category, template_id: templateId }).select("category"));
  }

  async listChecklistSubmissions(tenantId: string, query: SubmissionQuery = {}): Promise<ChecklistSubmission[]> {
    let q = this.client().from("checklist_submissions").select(SUBMISSION_COLUMNS).eq("tenant_id", tenantId);
    if (query.vehicleId) q = q.eq("vehicle_id", query.vehicleId);
    const rows = SupabaseProvider.check<SubmissionRow[]>(await q.order("submitted_at", { ascending: false }).limit(query.limit ?? 200));
    return rows.map(toSubmission);
  }

  async getChecklistSubmission(submissionId: string): Promise<ChecklistSubmissionDetail | null> {
    const db = this.client();
    const row = SupabaseProvider.check<SubmissionRow | SubmissionRow[] | null>(
      (await db.from("checklist_submissions").select(SUBMISSION_COLUMNS).eq("id", submissionId).maybeSingle()) as Res<SubmissionRow>,
    );
    if (!row || Array.isArray(row)) return null;
    const [answers, photos] = await Promise.all([
      db.from("checklist_answers").select(ANSWER_COLUMNS).eq("submission_id", submissionId).order("position"),
      db.from("checklist_photos").select(PHOTO_COLUMNS).eq("submission_id", submissionId),
    ]);
    return {
      submission: toSubmission(row),
      answers: SupabaseProvider.check<AnswerRow[]>(answers).map(toAnswer),
      photos: SupabaseProvider.check<PhotoRow[]>(photos).map(toPhoto),
    };
  }

  async listChecklistFaults(tenantId: string, query: FaultQuery = {}): Promise<ChecklistFault[]> {
    let q = this.client().from("checklist_faults").select(FAULT_COLUMNS).eq("tenant_id", tenantId);
    if (query.status) q = q.eq("status", query.status);
    if (query.vehicleId) q = q.eq("vehicle_id", query.vehicleId);
    const rows = SupabaseProvider.check<FaultRow[]>(await q.order("created_at", { ascending: false }).limit(query.limit ?? 500));
    return rows.map(toFault);
  }

  async resolveChecklistFault(faultId: string, note: string | null): Promise<void> {
    const trimmed = note?.trim() || null;
    if (trimmed && trimmed.length > CHECKLIST_LIMITS.resolutionNoteMax) throw new DataError("note_too_long", "note too long");
    SupabaseProvider.check(await this.client().rpc("resolve_checklist_fault", { p_fault: faultId, p_note: trimmed }));
  }

  async checklistPhotoUrl(path: string): Promise<string> {
    const { data, error } = await this.client().storage.from("checklist-photos").createSignedUrl(path, 600);
    if (error || !data?.signedUrl) throw new DataError("forbidden", error?.message ?? "no signed url");
    return data.signedUrl;
  }

  async listChecklistTokens(tenantId: string): Promise<ChecklistToken[]> {
    const rows = SupabaseProvider.check<TokenRow[]>((await this.client().rpc("checklist_tokens", { p_tenant: tenantId })) as Res<TokenRow[]>);
    return (Array.isArray(rows) ? rows : []).map(toToken);
  }

  async rotateChecklistToken(vehicleId: string): Promise<string> {
    return SupabaseProvider.check<string>((await this.client().rpc("rotate_checklist_token", { p_vehicle: vehicleId })) as Res<string>);
  }
}
